// @flow
import styled from "styled-components";
import { FORM_ERROR } from "final-form";
import { Form, Field } from "react-final-form";
import { useMutation } from "@apollo/client/react/hooks";
import { withRouter, type ContextRouter } from "react-router";
import { gql } from "@apollo/client";
import { media } from "@nested/ui";
import {
  TextInput,
  AddressPicker,
  mediumParagraph,
  mediumHeading,
} from "@nested/brand";
import { Button as OriginalButton, SmartLink } from "@nested/component-library";
import { getAnalyticsMetadata, sendAnalyticsEvent } from "@nested/analytics";
import { ACCOUNT_SIGNUP_SUCCESSFUL } from "@nested/analytics/events";
import { getConfig } from "@nested/config";
import { Sentry } from "@nested/isomorphic-sentry";
import { Checkbox } from "../Blog/Checkbox";

const Input = styled(TextInput)`
  margin-bottom: 20px;
`;

export const Button = styled(OriginalButton)`
  width: 100%;
  max-width: 489px;
  padding: 10px 20px;
  height: auto;
`;

const {
  ACCOUNT_URI,
  ACCOUNT_LOGIN_URI,
  ACCOUNT_PASSWORD_RESET_URI,
  MARKETING_SITE_URI,
} = getConfig();

const CREATE_ACCOUNT = gql`
  mutation CreateAccountRebranded(
    $input: CustomerOnboardingInput!
    $meta: VendorOnboardingMetaInput
  ) {
    customerOnboarding(input: $input, meta: $meta) {
      successful
      failureReason
      temporaryToken
    }
  }
`;

export const required = (value: ?boolean) => (value ? undefined : "Required");

export const StyledForm = styled.form`
  width: 100%;
  max-width: 560px;
  margin: auto;
  padding: 10px 0px;
  position: relative;
  ul {
    top: unset;
  }
  input {
    max-width: 100%;
    border-color: rgba(10, 66, 84, 0.4);
    color: rgba(10, 66, 84, 1);
  }
`;

export const EmailConsent = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
  display: inline-block;
`;

export const SignUp = styled.div`
  margin-bottom: 20px;
  vertical-align: top;
  width: 100%;
  ${media.tablet`
    display: inline-block;
    max-width: 200px;
    text-align: right;
  `}
`;

export const FormError = styled.p`
  color: ${({ theme }) => theme.palette.terracotta100};
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  padding: 20px;
`;

const Links = styled.p`
  font-size: 16px;
`;

export type Props = {|
  buttonText?: string,
  highContrast?: boolean,
  includeBlacklisted?: boolean,
|};

export type InnerProps = {
  ...ContextRouter<{}>,
  ...Props,
};

const createSubmitHandler = (mutation, pageUrl) => async (state) => {
  try {
    const result = await mutation({
      variables: {
        input: {
          customer: {
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            privacyPolicyConsent: state.emailConsent,
            originUrl: pageUrl,
            leadScore: "ZERO",
          },
          property: {
            addressId: state.address?.id,
            postcode: state.address?.postcode,
            recentlyMarketed: false,
          },
        },
        meta: getAnalyticsMetadata(),
      },
    });
    const validationError = result?.data?.customerOnboarding?.failureReason;

    const successfulMutation = result?.data?.customerOnboarding?.successful;

    if (!successfulMutation) {
      if (validationError === "EMAIL_INVALID") {
        return {
          [FORM_ERROR]:
            "The email address you have entered is not valid, please check it and try again.",
        };
      }
      throw new Error("Account creation failed");
    }

    sendAnalyticsEvent({
      event: ACCOUNT_SIGNUP_SUCCESSFUL,
      addressId: state.address?.id,
    });
    window.location.assign(`${ACCOUNT_URI}`);

    return undefined;
  } catch (e) {
    Sentry.captureException(e);
    return {
      [FORM_ERROR]: "Failed to create account, please try again later",
    };
  }
};

const SignUpFormComponent = ({
  highContrast,
  buttonText = "Sign up",
  history: {
    location: { pathname },
  },
}: InnerProps) => {
  const [createCustomerAccount] = useMutation(CREATE_ACCOUNT);
  const pageUrl = `${MARKETING_SITE_URI}${pathname}`;
  return (
    <Form
      onSubmit={createSubmitHandler(createCustomerAccount, pageUrl)}
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        address: null,
        emailConsent: false,
        originUrl: pageUrl,
      }}
    >
      {({
        handleSubmit,
        submitError,
        hasSubmitErrors,
        pristine,
        submitting,
        hasValidationErrors,
        submitSucceeded,
      }) => (
        <>
          {hasSubmitErrors && (
            <FormError data-test="sign-up-error" center>
              {submitError}
            </FormError>
          )}
          {submitSucceeded ? (
            <SuccessMessage>
              <h4 css={mediumHeading} data-test="sign-up-success">
                Please check your email
              </h4>
              <p css={mediumParagraph} center>
                We&apos;ve just sent you a link to view your personalised market
                report! You&apos;ll be asked to first set a password to keep
                your report secure.
              </p>
            </SuccessMessage>
          ) : (
            <StyledForm onSubmit={handleSubmit}>
              <div>
                <Field validate={required} name="firstName">
                  {({ input, meta }) => (
                    <Input
                      highContrast={highContrast}
                      data-test="sign-up-first-name"
                      placeholder="First name"
                      className="fs-exclude"
                      {...input}
                      {...meta}
                    />
                  )}
                </Field>
              </div>
              <div>
                <Field validate={required} name="lastName">
                  {({ input, meta }) => (
                    <Input
                      highContrast={highContrast}
                      data-test="sign-up-last-name"
                      placeholder="Surname"
                      {...input}
                      {...meta}
                      className="fs-exclude"
                    />
                  )}
                </Field>
              </div>
              <Field validate={required} name="email">
                {({ input, meta }) => (
                  <Input
                    highContrast={highContrast}
                    type="email"
                    data-test="sign-up-email"
                    placeholder="Enter your email"
                    className="fs-exclude"
                    {...input}
                    {...meta}
                  />
                )}
              </Field>
              <Field validate={required} name="address">
                {({ input, meta }) => (
                  <AddressPicker
                    dataTest="sign-up-address"
                    placeholder="Enter postcode"
                    includeBlacklisted
                    {...input}
                    {...meta}
                  />
                )}
              </Field>
              <EmailConsent>
                <Field type="checkbox" name="emailConsent">
                  {({ input, meta }) => (
                    <Checkbox
                      highContrast={highContrast}
                      data-test="sign-up-email-consent"
                      label="I’d like to receive emails relating to my property, the area and the housing market"
                      {...input}
                      {...meta}
                    />
                  )}
                </Field>
              </EmailConsent>
              <SignUp>
                <Button
                  data-test="sign-up-button"
                  disabled={pristine || hasValidationErrors || submitting}
                  type="primary"
                  inverted
                >
                  {buttonText}
                </Button>
              </SignUp>
              <Links center>
                Have an account already?{" "}
                <SmartLink to={ACCOUNT_LOGIN_URI}>Login</SmartLink>
                &nbsp;or&nbsp;
                <SmartLink to={ACCOUNT_PASSWORD_RESET_URI}>
                  reset your password
                </SmartLink>
                .
              </Links>
            </StyledForm>
          )}
        </>
      )}
    </Form>
  );
};

export const SignUpForm = withRouter<Props>(SignUpFormComponent);
